import React, { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '@/components/shared/SEOHead';
import { SectionHeading } from '@/components/shared/SharedComponents';
import { CalendarDays, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPage() {
  const { currentTheme: t, theme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API}/blog?status=published`);
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setPosts(data);
        } else {
          // fallback to mock data
          const { BLOG_POSTS } = await import('@/data/mock');
          setPosts(BLOG_POSTS.map((p, i) => ({ ...p, id: `mock-${i}` })));
        }
      } else {
        const { BLOG_POSTS } = await import('@/data/mock');
        setPosts(BLOG_POSTS.map((p, i) => ({ ...p, id: `mock-${i}` })));
      }
    } catch {
      const { BLOG_POSTS } = await import('@/data/mock');
      setPosts(BLOG_POSTS.map((p, i) => ({ ...p, id: `mock-${i}` })));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <SEOHead
        title="Blog - Recursos de Capital Humano y Bienestar Corporativo | Motivy"
        description="Consejos y estrategias para transformar tu cultura organizacional. Artículos sobre reconocimiento laboral, bienestar corporativo, gamificación y más."
        keywords="blog recursos humanos, bienestar corporativo, reconocimiento laboral, cultura organizacional, gamificación empresarial"
      />
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-12" style={{ background: t.heroBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>
            Blog Motivy
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-lg max-w-2xl mx-auto" style={{ color: t.textSecondary }}>
            Consejos y estrategias para transformar tu cultura organizacional.
          </motion.p>
        </div>
      </section>

      {/* Posts */}
      <section className="py-16" style={{ background: t.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="rounded-xl border overflow-hidden animate-pulse" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <div className="aspect-video" style={{ background: t.sectionAltBg }} />
                  <div className="p-5 space-y-3">
                    <div className="h-3 rounded w-24" style={{ background: t.sectionAltBg }} />
                    <div className="h-5 rounded w-full" style={{ background: t.sectionAltBg }} />
                    <div className="h-3 rounded w-3/4" style={{ background: t.sectionAltBg }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <motion.article
                  key={post.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-xl border overflow-hidden transition-all duration-300 group"
                  style={{ background: t.cardBg, borderColor: t.cardBorder }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 12px 32px ${t.shadowHover}`;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image || post.og_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs mb-3" style={{ color: t.textMuted }}>
                        <CalendarDays size={12} />
                        <time>{post.date || (post.published_at && new Date(post.published_at).toLocaleDateString('es-ES', { year:'numeric', month:'long', day:'numeric' }))}</time>
                      </div>
                      <h2 className="text-base font-semibold mb-2 line-clamp-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>{post.title}</h2>
                      <p className="text-sm line-clamp-3 mb-4" style={{ color: t.textSecondary }}>{post.excerpt || post.meta_description}</p>
                      <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: t.accent }}>
                        Leer más <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
